import { AsyncPipe, SlicePipe } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BetBuilderProvider } from '@cds/betting-offer/add-ons';
import { ResultState } from '@cds/betting-offer/domain-specific/bet-builder';
import {
    BetBuilderButtonSource,
    CrmPromoIdsMap,
    EventModel,
    EventOptionGroup,
    ExtendedDisplayType,
} from '@frontend/sports/betting-offer/feature/model';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChange, ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { ColumnLayoutService, ColumnSizeData } from '@frontend/sports/layout/feature';
import { OptionPanelThreshold } from '@frontend/sports/types/configuration';
import { DeviceService, MediaQueryService } from '@frontend/vanilla/core';
import { LaunchDarklyService } from '@frontend/vanilla/features/launch-darkly';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import { RxIf } from '@rx-angular/template/if';
import { times } from 'lodash-es';
import { BehaviorSubject, Observable, ReplaySubject, Subject, combineLatest, distinctUntilChanged, map, startWith, take } from 'rxjs';

import { BetBuilderTrackingValues } from '../bet-builder/bet-builder-tracking';
import { ShowSGPBadgeOnEDPLaunchDarklyKey } from '../bet-builder/bet-builder.constants';
import { BetBuilderConfigService } from '../bet-builder/services/bet-builder-config.service';
import { SourceOfPick } from '../betslip-base/models/pick-models';
import { DirectivesModule } from '../directives/directives.module';
import { OptionMarketSetData } from '../event-details-common/event-details-common.models';
import { OptionGroupTabindexKeeperService } from './option-group-tabindex-keeper.service';
import { OptionPanelComponent } from './option-panel.component';
import { isOptionVisible } from './precreated-group-option-utils';

export interface TemplatesWithThresholds {
    templates: StringDictionary<string>;
    thresholds: OptionPanelThreshold[];
}

@Component({
    selector: 'ms-option-group-list',
    templateUrl: 'option-group-list.component.html',
    providers: [OptionGroupTabindexKeeperService],
    imports: [DirectivesModule, RxIf, OptionPanelComponent, SlicePipe, AsyncPipe],
})
export class OptionGroupListComponent implements OnInit, AfterViewInit, OnChanges {
    @Input() event: EventModel;
    @Input() promoted: StringDictionary<string>;
    @Input() crmPromotedMap: StringDictionary<string> = {};
    @Input() crmPromoIdsMap: CrmPromoIdsMap = { templateIds: {}, markets: {}, options: {} };
    @Input() showSixPackLogosColor = false;
    @Input() showSixPackLogos = false;

    @Input() hideMarketHeader = false;

    @Input()
    set optionGroups(value: EventOptionGroup[]) {
        this._optionGroups$.next(value);
    }

    private _optionGroups$ = new ReplaySubject<EventOptionGroup[]>(1);

    @Input() selectedSetId: string | number;
    @Input() thresholds?: TemplatesWithThresholds;

    @Input()
    set optionPanelLimit(value: number | undefined) {
        this.optionPanelLimit$.next(value);
    }

    private optionPanelLimit$ = new BehaviorSubject<number | undefined>(undefined);

    private page$ = new BehaviorSubject<number>(1);

    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() isSGP?: boolean;

    @Input()
    set forceSingleColumn(value: boolean) {
        this.forceSingleColumn$.next(value);
    }

    private forceSingleColumn$ = new BehaviorSubject(false);

    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() hideEventCarousel?: boolean;
    @Input() showEventName = false;
    @Input() showEventTimer = false;
    @Input() showEventChevron = false;
    @Input() isLinkToEvent = true;
    @Input() toggleable = true;
    @Input() showMoreCounter = false;
    @Input() optionsLimitOverride?: number;
    @Input() babButtonSource?: BetBuilderButtonSource;
    @Input() betBuilderEpcotEnabled = false;
    @Input() source: string;
    @Input() appendTooltipToWidgetSlot = false;
    @Output() eventTitleClicked = new EventEmitter<EventModel>();

    @Input() set defaultExpandedMarketsCount(value: number | undefined) {
        this._defaultExpandedMarketsCount$.next(value);
    }

    optionMarketSetData = input<OptionMarketSetData>();

    private _defaultExpandedMarketsCount$ = new BehaviorSubject<number | undefined>(undefined);

    @HostBinding('class') className = 'option-group-list';

    marketOptionGroups$: Observable<EventOptionGroup[]> = combineLatest([this._optionGroups$, this._defaultExpandedMarketsCount$]).pipe(
        map(([optionGroups, defaultExpandedMarketsCount]) =>
            optionGroups && defaultExpandedMarketsCount && defaultExpandedMarketsCount > 0 ? optionGroups.slice(0, defaultExpandedMarketsCount) : [],
        ),
    );

    private readonly TWO_COLUMNS_MIN_WIDTH = 600;
    private readonly SINGLE_COLUMN_LIMIT_COUNT = 50;
    private readonly step = 10;
    private centerColumnSize$ = new BehaviorSubject(0);
    selectedSetIdChange: ISimpleChange<string | number> | undefined;

    render$ = new Subject();
    renderStrategy$ = this.render$.pipe(
        map(() => this.strategy.primaryStrategy),
        startWith('native'),
    );

    columns$: Observable<EventOptionGroup[][]> = combineLatest([
        this._optionGroups$,
        this.centerColumnSize$.pipe(distinctUntilChanged()),
        this.forceSingleColumn$.pipe(distinctUntilChanged()),
    ]).pipe(
        map(([optionGroups, centerColumnSize, forceSingleColumn]) => {
            const nrOfColumns =
                optionGroups &&
                optionGroups.length > 1 &&
                centerColumnSize > this.TWO_COLUMNS_MIN_WIDTH &&
                !forceSingleColumn &&
                ((this.mediaService.isActive('gt-xs') && !this.mediaService.isActive('xs-height')) || this.mediaService.isActive('gt-sm'))
                    ? 2
                    : 1;

            const columns = times(nrOfColumns, (index) => index);

            return columns.map((column, columnIndex) => optionGroups.filter((_, index) => index % columns.length === columnIndex));
        }),
    );

    limit$ = combineLatest([this.forceSingleColumn$, this.optionPanelLimit$, this.page$]).pipe(
        map(([forceSingleColumn, optionPanelLimit, page]) => {
            const step = forceSingleColumn ? (optionPanelLimit ?? this.SINGLE_COLUMN_LIMIT_COUNT) : (optionPanelLimit ?? this.step);

            return step * page;
        }),
    );

    vm$ = combineLatest({
        limit: this.limit$,
        columns: this.columns$,
        forceSingleColumn: this.forceSingleColumn$,
        marketOptionGroup: this.marketOptionGroups$,
    });

    shouldShowPrecreated = false;
    betBuilderDisplayType = ExtendedDisplayType.BetBuilder;
    private readonly destroyRef = inject(DestroyRef);

    showSGPBadge$: Observable<boolean>;
    private readonly launchDarklyService: LaunchDarklyService = inject(LaunchDarklyService);

    constructor(
        public deviceService: DeviceService,
        private mediaService: MediaQueryService,
        private columnLayoutService: ColumnLayoutService,
        private betBuilderConfigService: BetBuilderConfigService,
        private strategy: RxStrategyProvider,
    ) {}

    ngOnInit(): void {
        this.promoted = this.promoted || {};
        this.columnLayoutService.centerColumnSizeChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.onColumnSizeChanged);
        this.showSGPBadge$ = this.launchDarklyService.getFeatureFlagValue(ShowSGPBadgeOnEDPLaunchDarklyKey).pipe(take(1));
    }

    ngAfterViewInit(): void {
        this.columnLayoutService.resetMeasurement();
    }

    ngOnChanges(changes: ISimpleChanges<OptionGroupListComponent>): void {
        this.selectedSetIdChange = changes.selectedSetId;

        if (this.selectedSetIdChange) {
            if (!this.selectedSetIdChange.firstChange) {
                this.page$.next(1);
            }
        }

        this.shouldShowPrecreated = this.precreatedGroupsAvailable() || this.event.betBuilderInfo?.betBuilderProvider === BetBuilderProvider.Default;
    }

    getMore(): void {
        this.page$.next(this.page$.value + 1);
    }

    isCrmPromoted(optionGroup: EventOptionGroup): boolean {
        return !optionGroup.groupedMarkets && !optionGroup.subGroups && !!this.crmPromotedMap[optionGroup.originalId];
    }

    getThresholds(optionGroup: EventOptionGroup): OptionPanelThreshold[] | undefined {
        const hasThresholds = this.thresholds?.templates[optionGroup.templateId];

        return hasThresholds ? this.thresholds?.thresholds : undefined;
    }

    get inOverlay(): boolean {
        return this.betBuilderConfigService.showEntainUiInOverlay(this.event.sport.id) && !this.isEmbeddedUi;
    }

    get isEmbeddedUi(): boolean {
        return this.betBuilderConfigService.showEmbeddedUi() && this.source === BetBuilderTrackingValues.EventDetails;
    }

    private onColumnSizeChanged = (column: ColumnSizeData): void => {
        this.centerColumnSize$.next(column.width);
    };

    onEventTitleClick(): void {
        this.eventTitleClicked.emit();
    }

    getcollapsed(optionGroup: EventOptionGroup, marketOptionGroups: EventOptionGroup[]): boolean {
        if (this.event.betBuilderTradingV2FixtureId) {
            return marketOptionGroups.length > 0 && this.containsIdenticalOGPEventOptionGroup(optionGroup, marketOptionGroups);
        }

        return (
            (marketOptionGroups.length > 0 || this.defaultExpandedMarketsCount === 0) &&
            !marketOptionGroups.filter((x) => x.id === optionGroup.id).length
        );
    }

    private containsIdenticalOGPEventOptionGroup(optionGroup: EventOptionGroup, marketOptionGroups: EventOptionGroup[]): boolean {
        return !marketOptionGroups.filter(
            (x) =>
                x.id === optionGroup.id &&
                x.name === optionGroup.name &&
                ((x.options.length && x.options[0].longId === optionGroup.options[0].longId) ||
                    (x.subGroups && optionGroup.subGroups && x.subGroups[0].options[0].longId === optionGroup.subGroups[0].options[0].longId)),
        ).length;
    }

    private precreatedGroupsAvailable(): boolean {
        if (!this.event.optionGroups?.length || !this.event.precreatedOptionGroups?.length || this.event.live) {
            return false;
        }

        const visibleOptions = this.event.optionGroups
            .filter((og) => og.detailedGrouping && og.detailedGrouping.displayType && og.detailedGrouping.displayType === this.betBuilderDisplayType)
            .flatMap((filtered) => filtered.options.filter((o) => isOptionVisible(o)));

        return this.event.precreatedOptionGroups.some((g) => {
            if (this.event.betBuilderInfo?.betBuilderProvider !== BetBuilderProvider.BybFootball) {
                return (
                    g.builderOptionPricing.legInformation.length &&
                    g.builderOptionPricing.legInformation.every((l) => l.resultState === ResultState.Open) &&
                    g.options.every((o) => visibleOptions.some((vo) => vo.id === o.id))
                );
            }

            return g.options.every((o) => visibleOptions.some((vo) => vo.id === o.id));
        });
    }
}
